import {
  EffortLevel,
  ModelSpeed,
  getEffortDisplayLabel,
} from "./effortLevel.js";
import { Model, ModelLiteral } from "./Model.js";
import { ModelFeatureMap } from "./ModelFeatures.js";

/**
 * Resolve a model and optional effort to the display label shown in user-facing
 * eval surfaces.
 */
export function formatModelEffortLabel(
  model: string | null | undefined,
  effortLevel: string | null | undefined,
): string | null {
  if (model == null) return null;

  const modelLabel = ModelLiteral.guard(model)
    ? ModelFeatureMap[model].label
    : model;

  const effortLabel = getEffortDisplayLabel(effortLevel);
  return effortLabel == null ? modelLabel : `${modelLabel} (${effortLabel})`;
}

/**
 * Coerce a persisted thread model value for GraphQL and runtime resolution.
 * Returns null for values that are no longer recognized ModelName enum members.
 */
export function resolvePersistedModelName(
  model: string | null | undefined,
): Model | null {
  // Some historical threads may still have model set to null.
  // TODO(@zkirby): clean up this logic after >3 months when historical
  // threads have been gc'd.
  if (model == null) return null;
  return ModelLiteral.guard(model) ? model : null;
}

/**
 * Find the matching, currently-usable picker option for a model name. Returns
 * `undefined` when `model` is null/"auto" or when the model is absent or
 * disabled in `options`.
 */
export function findAvailableModelOption<
  T extends { model: Model | null; disabled: boolean },
>(options: readonly T[], model: Model | null): T | undefined {
  if (model == null) {
    return undefined;
  }
  return options.find((opt) => opt.model === model && !opt.disabled);
}

export interface ModelSelection {
  model: Model | null;
  effortLevel: EffortLevel | null;
  speed: ModelSpeed | null;
}

export const AUTO_MODEL_SELECTION: ModelSelection = {
  model: null,
  effortLevel: null,
  speed: null,
};

/**
 * Resolve a stored/selected (model, effort, speed) against the org's currently
 * available picker options, mirroring the server's `resolveUserSelection`:
 * returns "auto" (all-null) unless the model is available, the effort is
 * supported, and — for fast — a fast variant exists for that effort (otherwise
 * the speed drops to standard). Shared by the org default-model setting row, the
 * prompt-bar seeding hook, and anywhere else a stored selection is validated on
 * read so the answer never diverges.
 */
export function resolveAvailableModelSelection<
  T extends {
    model: Model | null;
    disabled: boolean;
    supportedEfforts: readonly EffortLevel[];
    supportedFastEfforts?: readonly EffortLevel[] | null;
  },
>(selection: ModelSelection, options: readonly T[]): ModelSelection {
  const available = findAvailableModelOption(options, selection.model);
  if (
    available == null ||
    selection.effortLevel == null ||
    !available.supportedEfforts.includes(selection.effortLevel)
  ) {
    return AUTO_MODEL_SELECTION;
  }
  const shouldUseFast =
    selection.speed === "fast" &&
    (available.supportedFastEfforts ?? []).includes(selection.effortLevel);
  return {
    model: selection.model,
    effortLevel: selection.effortLevel,
    speed: shouldUseFast ? "fast" : null,
  };
}

export type ThreadModelFields = Partial<{
  auto: boolean | null;
  model: string | null;
}>;

/** The model the user explicitly picked or 'null' if for deprecated and 'auto' models */
export const threadPickerModel = (thread: ThreadModelFields): Model | null =>
  thread.auto ? null : resolvePersistedModelName(thread.model);

/**
 * Whether the thread's model is Auto rather than an explicit picker selection.
 *
 * True for Auto threads, legacy threads with no persisted model, and threads
 * whose persisted model is no longer a recognized ModelName.
 */
export const threadModelIsAuto = (thread: ThreadModelFields): boolean =>
  thread.auto === true || resolvePersistedModelName(thread.model) == null;

/**
 * Resolves the display label for a thread's model:
 * 1. 'Auto' for deprecated models
 * 2. 'Auto (<resolved model>)' for auto threads
 * 3. '<resolved model>' for explicit selections
 */
export function formatThreadModelLabel({
  auto,
  model,
}: ThreadModelFields): string {
  const resolved = resolvePersistedModelName(model);
  if (resolved == null) return "Auto";
  const label = ModelFeatureMap[resolved].label;
  return auto ? `Auto (${label})` : label;
}
